import { suggestSectionKeys } from '../utils/OrchestralLogic/ModulationEngine.js';
import { MusicEngine, GenerationProfile } from '../utils/MusicEngine';
import { useState, useCallback } from 'react';

export const useMusicEngine = () => {
    const [engine] = useState(() => new MusicEngine());
    const [composition, setComposition] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [structure, setStructure] = useState([
        { type: 'intro', bars: 4 },
        { type: 'verse', bars: 8 },
        { type: 'chorus', bars: 8 },
        { type: 'outro', bars: 4 }
    ]);
    const [currentEnsemble, setCurrentEnsemble] = useState('rock_band');
    const [currentSubGenre, setCurrentSubGenre] = useState('classic_rock');
    const [traitOverrides, setTraitOverrides] = useState({});

    // Populate keys on structure update
    const [rootKey, setRootKey] = useState('C');
    const [scaleType, setScaleType] = useState('major');

    const addSection = useCallback((type, bars) => {
        setStructure(prev => {
            const next = [...prev, { type, bars: bars || 8 }];
            return suggestSectionKeys(next, rootKey, scaleType);
        });
    }, [rootKey, scaleType]);

    const removeSection = useCallback((index) => {
        setStructure(prev => prev.filter((_, i) => i !== index));
    }, []);

    const updateSection = useCallback((index, newData) => {
        setStructure(prev => prev.map((s, i) => i === index ? { ...s, ...newData } : s));
    }, []);

    const setTraitOverride = useCallback((key, value) => {
        setTraitOverrides(prev => {
            if (value === 'match') {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            }
            return { ...prev, [key]: value };
        });
    }, []);

    const registerNoteListener = useCallback((cb) => {
        return engine.registerNoteListener(cb);
    }, [engine]);

    const generateComposition = useCallback((profileOptions, context) => {
        setIsGenerating(true);
        try {
            const safeContext = (context && typeof context === 'object') ? context : {};
            const finalContext = {
                ...safeContext,
                structure: suggestSectionKeys(safeContext.structure || structure, safeContext.rootKey || 'C', safeContext.scaleType || 'major'),
                ensembleKey: context.ensembleKey || currentEnsemble
            };
            const finalProfile = new GenerationProfile({
                ...profileOptions,
                harmonySource: traitOverrides.harmony || profileOptions.harmonySource || currentSubGenre,
                rhythmSource: traitOverrides.rhythm || profileOptions.rhythmSource || currentSubGenre,
                voicingSource: traitOverrides.voicingSource || profileOptions.voicingSource || currentSubGenre
            });

            const result = engine.generate(finalProfile, finalContext);
            setComposition(result);
            return result;
        } catch (error) {
            console.error('[useMusicEngine] Generation Failed:', error);
            return null;
        } finally {
            setIsGenerating(false);
        }
    }, [engine, structure, currentEnsemble, currentSubGenre, traitOverrides, rootKey, scaleType]);

    return {
        generateComposition,
        composition,
        setComposition,
        isGenerating,
        setIsGenerating,
        structure,
        setStructure,
        addSection,
        removeSection,
        updateSection,
        currentEnsemble,
        setCurrentEnsemble,
        currentSubGenre,
        setCurrentSubGenre,
        traitOverrides,
        setTraitOverrides,
        setTraitOverride,
        registerNoteListener,
        engine
    };
};
